import React, { useEffect, useState } from 'react';
import {
  BadgeCheck,
  Bird,
  Building2,
  ChevronRight,
  Clock3,
  Droplets,
  Factory,
  Home,
  Leaf,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  Phone,
  ShieldCheck,
  Sparkles,
  SprayCan,
  X
} from 'lucide-react';

const logo = `${process.env.PUBLIC_URL}/logo.png`;
const eyeWatermark = `${process.env.PUBLIC_URL}/eye-watermark.jpg`;

const services = [
  {
    title: 'Pest Control',
    description: 'Cockroaches, ants, termites, rodents and more.',
    icon: SprayCan
  },
  {
    title: 'Hygiene Services',
    description: 'Professional hygiene solutions for healthier spaces.',
    icon: Droplets
  },
  {
    title: 'Carpet Cleaning',
    description: 'Deep cleaning for homes, offices, and commercial spaces.',
    icon: Sparkles
  },
  {
    title: 'Weed Control',
    description: 'Weed treatment and control for outdoor areas.',
    icon: Leaf
  },
  {
    title: 'Bird Proofing',
    description: 'Practical bird proofing solutions for properties.',
    icon: Bird
  }
];

const sectors = [
  {
    title: 'Residential',
    description: 'Homes, flats, complexes, and family spaces.',
    icon: Home
  },
  {
    title: 'Commercial',
    description: 'Shops, offices, restaurants, schools, and facilities.',
    icon: Building2
  },
  {
    title: 'Industrial',
    description: 'Warehouses, workshops, plants, and operating sites.',
    icon: Factory
  }
];

const reasons = [
  'SAPCA Members',
  'Available 24/7',
  'Residential, Commercial & Industrial',
  'Health-Focused Pest Solutions',
  'Professional Service',
  'Reliable Communication'
];

const navItems = [
  ['Home', 'home'],
  ['Services', 'services'],
  ['Sectors', 'sectors'],
  ['Why Choose Us', 'why'],
  ['Contact', 'contact']
];

function App() {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    document.title = 'Eye of Africa Pest Control | Pest Control Services in Gauteng';
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMenuOpen(false);
  };

  return (
    <div className="site-shell">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        :root {
          --ink: #15120d;
          --ink-soft: #42382b;
          --muted: #776d61;
          --gold: #b7793c;
          --gold-deep: #7c461f;
          --gold-soft: #dfb16f;
          --cream: #f8f4ec;
          --paper: #fffdf8;
          --line: #e2d6c2;
          --white: #ffffff;
        }

        .site-shell {
          min-height: 100vh;
          color: var(--ink);
          background:
            radial-gradient(circle at 10% 0%, rgba(183, 121, 60, 0.18), transparent 28rem),
            linear-gradient(180deg, #fffaf0 0%, var(--cream) 42%, #ffffff 100%);
          overflow-x: hidden;
        }

        .container {
          width: min(1160px, calc(100% - 40px));
          margin: 0 auto;
        }

        .nav {
          position: sticky;
          top: 0;
          z-index: 30;
          border-bottom: 1px solid rgba(226, 214, 194, 0.8);
          background: rgba(255, 253, 248, 0.92);
          backdrop-filter: blur(18px);
        }

        .nav-inner {
          min-height: 78px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
        }

        .brand {
          display: inline-flex;
          align-items: center;
          gap: 14px;
          color: var(--ink);
          text-decoration: none;
          font-weight: 800;
          letter-spacing: 0;
          border: 0;
          background: transparent;
          cursor: pointer;
        }

        .brand img {
          width: 164px;
          height: auto;
          border-radius: 0;
          box-shadow: none;
        }

        .brand span {
          display: grid;
          line-height: 1.05;
          min-width: 0;
        }

        .brand small {
          margin-top: 4px;
          color: var(--gold-deep);
          font-size: 12px;
          font-weight: 700;
        }

        .nav-links {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .nav-links button,
        .menu-button {
          border: 0;
          background: transparent;
          color: var(--ink-soft);
          cursor: pointer;
          font: inherit;
          font-weight: 700;
        }

        .nav-links button {
          padding: 12px 14px;
          border-radius: 999px;
          transition: background 180ms ease, color 180ms ease, transform 180ms ease;
        }

        .nav-links button:hover {
          color: var(--ink);
          background: rgba(199, 150, 69, 0.14);
          transform: translateY(-1px);
        }

        .menu-button {
          display: none;
          width: 44px;
          height: 44px;
          align-items: center;
          justify-content: center;
          border-radius: 14px;
          background: var(--ink);
          color: var(--white);
        }

        .hero {
          position: relative;
          padding: 92px 0 76px;
        }

        .hero-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.05fr) minmax(320px, 0.95fr);
          gap: 48px;
          align-items: center;
        }

        .eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin: 0 0 18px;
          padding: 9px 13px;
          border: 1px solid rgba(183, 121, 60, 0.34);
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.72);
          color: var(--gold-deep);
          font-size: 14px;
          font-weight: 800;
        }

        h1,
        h2,
        h3,
        p {
          margin-top: 0;
        }

        h1 {
          max-width: 820px;
          margin-bottom: 18px;
          font-size: clamp(44px, 6vw, 78px);
          line-height: 0.98;
          letter-spacing: 0;
        }

        .lead {
          max-width: 680px;
          color: var(--ink-soft);
          font-size: clamp(18px, 2vw, 21px);
          line-height: 1.65;
        }

        .tagline {
          margin: 16px 0 0;
          color: var(--gold-deep);
          font-weight: 800;
        }

        .cta-row {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 32px;
        }

        .button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          min-height: 48px;
          padding: 13px 18px;
          border: 1px solid transparent;
          border-radius: 14px;
          color: var(--ink);
          text-decoration: none;
          font-weight: 800;
          transition: transform 180ms ease, box-shadow 180ms ease, background 180ms ease;
        }

        .button.primary {
          background: var(--ink);
          color: var(--white);
          box-shadow: 0 18px 34px rgba(21, 18, 13, 0.22);
        }

        .button.gold {
          background: linear-gradient(135deg, var(--gold-soft), var(--gold));
          color: #140e05;
          box-shadow: 0 18px 34px rgba(138, 95, 35, 0.22);
        }

        .button.light {
          border-color: var(--line);
          background: rgba(255, 255, 255, 0.78);
        }

        .button:hover {
          transform: translateY(-2px);
          box-shadow: 0 18px 34px rgba(21, 18, 13, 0.18);
        }

        .hero-card {
          position: relative;
          overflow: hidden;
          min-height: 520px;
          padding: 32px;
          border: 1px solid rgba(226, 214, 194, 0.9);
          border-radius: 32px;
          background:
            linear-gradient(145deg, rgba(21, 18, 13, 0.94), rgba(48, 32, 19, 0.96)),
            var(--ink);
          color: var(--white);
          box-shadow: 0 32px 80px rgba(21, 18, 13, 0.22);
        }

        .hero-card::before {
          content: "";
          position: absolute;
          inset: 24px;
          border: 1px solid rgba(223, 177, 111, 0.34);
          border-radius: 24px;
        }

        .inspection-panel {
          position: relative;
          z-index: 1;
          display: grid;
          min-height: 456px;
          align-content: space-between;
        }

        .mark-wrap {
          display: flex;
          justify-content: center;
          padding: 42px 0 20px;
        }

        .mark-wrap img {
          width: min(430px, 82vw);
          border-radius: 18px;
          background: var(--white);
          padding: 20px;
          box-shadow: 0 28px 50px rgba(0, 0, 0, 0.28);
        }

        .status-list {
          display: grid;
          gap: 12px;
          padding: 0;
          margin: 0;
          list-style: none;
        }

        .status-list li {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 13px 14px;
          border-radius: 16px;
          background: rgba(255, 255, 255, 0.08);
          color: rgba(255, 255, 255, 0.88);
          font-weight: 700;
        }

        section {
          scroll-margin-top: 94px;
        }

        .section {
          padding: 74px 0;
        }

        .section-head {
          display: flex;
          justify-content: space-between;
          align-items: end;
          gap: 24px;
          margin-bottom: 28px;
        }

        .section-head h2 {
          margin-bottom: 0;
          font-size: clamp(30px, 4vw, 48px);
          line-height: 1.05;
          letter-spacing: 0;
        }

        .section-head p {
          max-width: 520px;
          margin-bottom: 0;
          color: var(--muted);
          line-height: 1.7;
        }

        .card-grid {
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: 16px;
        }

        .service-card,
        .sector-card,
        .reason-card,
        .contact-card {
          border: 1px solid var(--line);
          border-radius: 22px;
          background: rgba(255, 253, 248, 0.88);
          box-shadow: 0 18px 48px rgba(21, 18, 13, 0.07);
          transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
        }

        .service-card {
          min-height: 244px;
          padding: 22px;
        }

        .service-card:hover,
        .sector-card:hover,
        .reason-card:hover,
        .contact-card:hover {
          transform: translateY(-5px);
          border-color: rgba(199, 150, 69, 0.55);
          box-shadow: 0 24px 58px rgba(21, 18, 13, 0.12);
        }

        .icon-box {
          display: inline-flex;
          width: 52px;
          height: 52px;
          align-items: center;
          justify-content: center;
          margin-bottom: 28px;
          border-radius: 16px;
          background: linear-gradient(145deg, #16130e, #382314);
          color: var(--gold-soft);
        }

        .service-card h3,
        .sector-card h3 {
          margin-bottom: 10px;
          font-size: 21px;
        }

        .service-card p,
        .sector-card p,
        .contact-card p {
          margin-bottom: 0;
          color: var(--muted);
          line-height: 1.6;
        }

        .sector-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 18px;
        }

        .sector-card {
          padding: 26px;
        }

        .quote-band {
          position: relative;
          overflow: hidden;
          border-radius: 32px;
          background:
            linear-gradient(135deg, rgba(21, 18, 13, 0.96), rgba(61, 38, 20, 0.94)),
            var(--ink);
          color: var(--white);
          padding: 42px;
          box-shadow: 0 30px 70px rgba(21, 18, 13, 0.2);
        }

        .quote-band::after {
          content: "";
          position: absolute;
          right: -90px;
          top: -120px;
          width: 280px;
          height: 280px;
          border: 44px solid rgba(223, 177, 111, 0.17);
          border-radius: 999px;
        }

        .quote-band::before {
          content: "";
          position: absolute;
          inset: 0;
          background: var(--quote-watermark) right center / min(620px, 72vw) auto no-repeat;
          opacity: 0.14;
          mix-blend-mode: screen;
        }

        .quote-content {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 28px;
          align-items: center;
        }

        .quote-content h2 {
          margin-bottom: 14px;
          font-size: clamp(30px, 4vw, 46px);
        }

        .quote-content p {
          max-width: 760px;
          margin-bottom: 0;
          color: rgba(255, 255, 255, 0.78);
          font-size: 18px;
          line-height: 1.7;
        }

        .reason-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 16px;
        }

        .reason-card {
          display: flex;
          align-items: center;
          gap: 12px;
          min-height: 82px;
          padding: 18px;
          font-weight: 800;
        }

        .reason-card svg {
          flex: 0 0 auto;
          color: var(--gold-deep);
        }

        .contact-layout {
          display: grid;
          grid-template-columns: minmax(0, 0.88fr) minmax(360px, 1.12fr);
          gap: 18px;
        }

        .contact-card {
          padding: 28px;
        }

        .contact-card.dark {
          background: var(--ink);
          color: var(--white);
        }

        .contact-card.dark p {
          color: rgba(255, 255, 255, 0.78);
        }

        .contact-list {
          display: grid;
          gap: 12px;
          margin-top: 22px;
        }

        .contact-link,
        .contact-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          border: 1px solid rgba(226, 214, 194, 0.7);
          border-radius: 16px;
          color: var(--ink);
          background: rgba(255, 255, 255, 0.72);
          text-decoration: none;
          font-weight: 800;
        }

        .contact-link:hover {
          border-color: var(--gold);
          background: #fff8ea;
        }

        .contact-card.dark .contact-link {
          color: var(--white);
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(255, 255, 255, 0.14);
        }

        .contact-card.dark .contact-link:hover {
          background: rgba(199, 150, 69, 0.18);
        }

        footer {
          padding: 32px 0;
          border-top: 1px solid var(--line);
          background: var(--ink);
          color: rgba(255, 255, 255, 0.78);
        }

        .footer-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
        }

        .footer-inner strong {
          display: block;
          color: var(--white);
          margin-bottom: 4px;
        }

        .footer-links {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        .footer-links a {
          color: rgba(255, 255, 255, 0.78);
          text-decoration: none;
          font-weight: 700;
        }

        @media (max-width: 980px) {
          .hero-grid,
          .contact-layout,
          .quote-content {
            grid-template-columns: 1fr;
          }

          .hero {
            padding-top: 64px;
          }

          .hero-card {
            min-height: auto;
          }

          .card-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .sector-grid,
          .reason-grid {
            grid-template-columns: 1fr;
          }

          .section-head {
            display: block;
          }

          .section-head h2 {
            margin-bottom: 14px;
          }
        }

        @media (max-width: 760px) {
          .container {
            width: min(100% - 28px, 1160px);
          }

          .nav-inner {
            min-height: 70px;
          }

          .brand img {
            width: 118px;
            height: auto;
          }

          .brand strong {
            font-size: 15px;
          }

          .brand small {
            display: none;
          }

          .menu-button {
            display: inline-flex;
          }

          .nav-links {
            position: absolute;
            left: 14px;
            right: 14px;
            top: 78px;
            display: grid;
            padding: 12px;
            border: 1px solid var(--line);
            border-radius: 18px;
            background: var(--paper);
            box-shadow: 0 20px 48px rgba(21, 18, 13, 0.16);
            transform-origin: top;
            transform: scaleY(0.96);
            opacity: 0;
            pointer-events: none;
            transition: opacity 160ms ease, transform 160ms ease;
          }

          .nav-links.open {
            transform: scaleY(1);
            opacity: 1;
            pointer-events: auto;
          }

          .nav-links button {
            width: 100%;
            text-align: left;
            border-radius: 12px;
          }

          h1 {
            font-size: clamp(38px, 13vw, 54px);
          }

          .cta-row,
          .footer-inner {
            display: grid;
          }

          .button {
            width: 100%;
          }

          .card-grid {
            grid-template-columns: 1fr;
          }

          .section {
            padding: 54px 0;
          }

          .quote-band {
            padding: 28px;
            border-radius: 24px;
          }
        }
      `}</style>

      <nav className="nav" aria-label="Primary navigation">
        <div className="container nav-inner">
          <button className="brand" onClick={() => scrollToSection('home')} aria-label="Go to home">
            <img src={logo} alt="Eye of Africa Pest Control logo" />
            <span>
              <strong>Professional Pest Control</strong>
              <small>Health-focused pest solutions</small>
            </span>
          </button>

          <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
            {navItems.map(([label, id]) => (
              <button key={id} type="button" onClick={() => scrollToSection(id)}>
                {label}
              </button>
            ))}
          </div>

          <button
            className="menu-button"
            type="button"
            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <main>
        <section id="home" className="hero">
          <div className="container hero-grid">
            <div>
              <p className="eyebrow">
                <ShieldCheck size={18} />
                SAPCA members serving residential, commercial, and industrial clients across Gauteng
              </p>
              <h1>Professional Pest Control You Can Trust</h1>
              <p className="lead">
                Eye of Africa Pest Control provides reliable pest control, hygiene, cleaning,
                weed control, and bird proofing services for residential, commercial, and
                industrial clients.
              </p>
              <p className="tagline">We focus on pests to keep our clients healthier.</p>

              <div className="cta-row" aria-label="Primary contact actions">
                <a className="button primary" href="tel:+27119254615">
                  <Phone size={19} />
                  Call 011 925 4615
                </a>
                <a className="button gold" href="https://wa.me/27790959784">
                  <MessageCircle size={19} />
                  WhatsApp 079 095 9784
                </a>
                <a className="button light" href="mailto:eyeofafrica@gmail.com">
                  <Mail size={19} />
                  Email
                </a>
                <button className="button light" type="button" onClick={() => scrollToSection('services')}>
                  View Services
                  <ChevronRight size={19} />
                </button>
              </div>
            </div>

            <aside className="hero-card" aria-label="Inspection and membership highlights">
              <div className="inspection-panel">
                <div className="mark-wrap">
                  <img src={logo} alt="Eye of Africa Pest Control logo with eye mark" />
                </div>
                <ul className="status-list">
                  <li>
                    <BadgeCheck size={20} />
                    South Africa Pest Control Association members
                  </li>
                  <li>
                    <Clock3 size={20} />
                    Available 24/7
                  </li>
                  <li>
                    <MapPin size={20} />
                    Gauteng service coverage with a Tembisa office address
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </section>

        <section id="services" className="section" aria-labelledby="services-heading">
          <div className="container">
            <div className="section-head">
              <h2 id="services-heading">Services</h2>
              <p>
                Practical, professional services for healthier indoor spaces and better controlled
                outdoor areas.
              </p>
            </div>
            <div className="card-grid">
              {services.map(({ title, description, icon: Icon }) => (
                <article className="service-card" key={title}>
                  <span className="icon-box" aria-hidden="true">
                    <Icon size={27} />
                  </span>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="sectors" className="section" aria-labelledby="sectors-heading">
          <div className="container">
            <div className="section-head">
              <h2 id="sectors-heading">Sectors We Service</h2>
              <p>
                Eye of Africa Pest Control supports homes, businesses, and industrial sites with
                treatment recommendations suited to each property.
              </p>
            </div>
            <div className="sector-grid">
              {sectors.map(({ title, description, icon: Icon }) => (
                <article className="sector-card" key={title}>
                  <span className="icon-box" aria-hidden="true">
                    <Icon size={27} />
                  </span>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section" aria-labelledby="inspection-heading">
          <div className="container">
            <div className="quote-band" style={{ '--quote-watermark': `url(${eyeWatermark})` }}>
              <div className="quote-content">
                <div>
                  <h2 id="inspection-heading">Request a Site Inspection</h2>
                  <p>
                    Every property is different. Eye of Africa Pest Control inspects the area first,
                    then provides the correct treatment recommendation and quote. Pricing is provided
                    after site inspection.
                  </p>
                </div>
                <a className="button gold" href="https://wa.me/27790959784?text=Hello%20Eye%20of%20Africa%20Pest%20Control%2C%20I%20would%20like%20to%20request%20a%20site%20inspection.">
                  Request an inspection for an accurate quote
                </a>
              </div>
            </div>
          </div>
        </section>

        <section id="why" className="section" aria-labelledby="why-heading">
          <div className="container">
            <div className="section-head">
              <h2 id="why-heading">Why Choose Us</h2>
              <p>
                A dependable service partner with clear communication, professional standards, and
                health-focused pest solutions.
              </p>
            </div>
            <div className="reason-grid">
              {reasons.map((reason) => (
                <div className="reason-card" key={reason}>
                  <ShieldCheck size={22} />
                  {reason}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="section" aria-labelledby="contact-heading">
          <div className="container contact-layout">
            <div className="contact-card dark">
              <p className="eyebrow">
                <Phone size={18} />
                Open 24/7
              </p>
              <h2 id="contact-heading">Get help from Eye of Africa Pest Control</h2>
              <p>
                Call, WhatsApp, or email to request an inspection for residential, commercial, or
                industrial pest control and hygiene services.
              </p>
              <div className="contact-list">
                <a className="contact-link" href="tel:+27119254615">
                  <Phone size={20} />
                  011 925 4615
                </a>
                <a className="contact-link" href="https://wa.me/27790959784">
                  <MessageCircle size={20} />
                  WhatsApp 079 095 9784
                </a>
                <a className="contact-link" href="https://wa.me/27781636503">
                  <MessageCircle size={20} />
                  WhatsApp 078 163 6503
                </a>
              </div>
            </div>

            <div className="contact-card">
              <h2>Contact Details</h2>
              <div className="contact-list">
                <a className="contact-link" href="mailto:eyeofafrica@gmail.com">
                  <Mail size={20} />
                  eyeofafrica@gmail.com
                </a>
                <div className="contact-item">
                  <MapPin size={20} />
                  347 Malabo Street, Ehlanzeni Section, Tembisa 1632
                </div>
                <div className="contact-item">
                  <Clock3 size={20} />
                  Opening: 24/7
                </div>
                <div className="contact-item">
                  <BadgeCheck size={20} />
                  SAPCA members: South Africa Pest Control Association
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer>
        <div className="container footer-inner">
          <div>
            <strong>Eye of Africa Pest Control</strong>
            We focus on pests to keep our clients healthier.
          </div>
          <div className="footer-links">
            <a href="tel:+27119254615">011 925 4615</a>
            <a href="https://wa.me/27790959784">079 095 9784</a>
            <a href="mailto:eyeofafrica@gmail.com">eyeofafrica@gmail.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
